import {
  AgFinancing,
  AgFinancingPhase3Status,
  AgFinancingPhaseStatus,
  AgStateName,
  BacenReport,
  BackgroundCheck,
  CreditApprovalData,
  OpportunityContactRole,
  OpportunityType,
  RiskClassificationResult,
  SerasaReport
} from '@indigo-ag/schema';
import i18next from 'core/i18n';
import { Dictionary } from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { Redirect, Route, Switch, useHistory, useLocation, useRouteMatch } from 'react-router-dom';

import { UserContext } from 'core/components/GateWay/GateWay';
import { FinanceProcessContext } from 'features/brazil-grower/pages/BrazilGrowersDetail';
import { CheckList, FinancingProcessUtil, IStep, Permissions, Stepper, utils, Phase2Util, Phase3Util } from 'shared';
import styled from 'styled-components';
import { Phase1 } from '../BrazilGrowerFinance/phases/Phase1';
import { Phase3 } from '../BrazilGrowerFinance/phases/Phase3';
import { Phase2 } from '../BrazilGrowerFinance/phases/phase2/Phase2';
import { Phase4 } from '../BrazilGrowerFinance/phases/phase4/Phase4';
import { getBackgroundCheckMap, getUniqueContactRoles } from '../BrazilGrowerFinance/utils/backgroundCheck.util';
import { Phase1Submenu } from './Phase1Submenu';
import { Phase1MenuProvider } from './context/Phase1MenuContext';
import { ListItem, Phase1MenuItem } from './context/Phase1.types';
import {
  checkIfDisablePhase2,
  getBackgroundCheckItems,
  getBackgroundCheckSubItems,
  getFundsApprovalItems,
  getSerasaBacenSubItems,
  getStatus1,
  getStatus2,
  getStatus3,
  getStatus4,
  isOpportunityAlternativeFlow,
  getPhase2Title
} from './Timeline.utils';

type PhaseRoutesStageName = [
  AgStateName.PHASE_1,
  AgStateName.PHASE_2,
  AgStateName.PHASE_3,
  AgStateName.PHASE_4,
  AgStateName.ON_HOLD,
  AgStateName.CLOSED
][number];

export const PhaseRoutes: Record<PhaseRoutesStageName, string> = {
  [AgStateName.PHASE_1]: 'phase01',
  [AgStateName.PHASE_2]: 'phase02',
  [AgStateName.PHASE_3]: 'phase03',
  [AgStateName.PHASE_4]: 'phase04',
  [AgStateName.ON_HOLD]: 'phase02',
  [AgStateName.CLOSED]: 'phase01'
};

const ChecklistLink = styled.a`
  color: #303c41;
`;

// Get correct path for a given step, if step does not have path look for parent path
const getStepPath = (allSteps: IStep[], step: IStep): string | null => {
  if (step.path) {
    return step.path;
  } else {
    const parent = allSteps.find(s => {
      return !!s.subSteps?.find(sub => sub.id === step.id);
    });

    if (parent?.path) {
      return parent.path;
    } else {
      return null;
    }
  }
};

const getStepsFromAgFinancing = (
  agFinancing: AgFinancing,
  opportunityContactRoles?: OpportunityContactRole[],
  backgroundCheckMap?: Dictionary<BackgroundCheck>,
  serasaReport?: SerasaReport,
  bacenReport?: BacenReport,
  contactRiskClassification?: RiskClassificationResult,
  canViewDecisionSheet?: boolean,
  creditApprovalDataRound?: CreditApprovalData['round']
): IStep[] => {
  // TODO: could define component on each route to create routes dynamically

  const hasPromissoryNote = Phase2Util.hasPromissoryNote(agFinancing);
  const hasConditionals =
    agFinancing.importedConditionals?.length || creditApprovalDataRound?.creditConditionals.length ? true : false;

  const backgroundCheckItems = getBackgroundCheckItems({ opportunityContactRoles, backgroundCheckMap, agFinancing });

  const backgroundCheckSubItems = getBackgroundCheckSubItems(backgroundCheckItems);
  const serasaBacenSubItems = getSerasaBacenSubItems(backgroundCheckItems, serasaReport, bacenReport);
  const fundsApprovalItems = getFundsApprovalItems(backgroundCheckItems, contactRiskClassification);

  const phase1SubSteps: ListItem[] = [
    {
      id: '1.1',
      title: i18next.t('preAnalysisBackgroundCheck'),
      type: 'backgroundCheck',
      submenuItems: backgroundCheckSubItems
    }
  ];

  const isAlternativeFlow = isOpportunityAlternativeFlow(agFinancing);
  const disablePhase2 = checkIfDisablePhase2(agFinancing);
  const showGeoreferencing =
    !hasPromissoryNote &&
    agFinancing?.opportunityType !== OpportunityType.SUPPLIER &&
    agFinancing?.opportunityType !== OpportunityType.BARTER_LIMIT;

  const isPhase3ValidatingDone =
    agFinancing?.phase3 && Phase3Util.isValidatingDone(agFinancing.phase3, hasConditionals);

  if (!isAlternativeFlow) {
    phase1SubSteps.push(
      {
        id: '1.2',
        title:
          agFinancing?.opportunityType === OpportunityType.SUPPLIER
            ? i18next.t('preAnalysisSerasa')
            : i18next.t('preAnalysisSerasaBacen'),
        type: 'preAnalysisSerasa',
        submenuItems: serasaBacenSubItems
      },
      ...(agFinancing?.opportunityType !== OpportunityType.SUPPLIER
        ? [
            {
              id: '1.3',
              title: i18next.t('preAnalysisFunds'),
              type: 'preAnalysisFunds' as Phase1MenuItem,
              submenuItems: fundsApprovalItems
            }
          ]
        : [])
    );
  }

  return [
    {
      id: '1',
      title: i18next.t('preAnalysis'),
      completed: agFinancing?.phase1?.status === AgFinancingPhaseStatus.CLEAR,
      path: PhaseRoutes[AgStateName.PHASE_1],
      status: getStatus1(agFinancing.phase1),
      component: <Phase1 phaseInfo={agFinancing.phase1} />,
      contentComponent: <Phase1Submenu items={phase1SubSteps} />
    },
    {
      id: '2',
      title: getPhase2Title(agFinancing),
      path: PhaseRoutes[AgStateName.PHASE_2],
      status: getStatus2(agFinancing.phase2),
      disabled: disablePhase2,
      component: <Phase2 phaseInfo={agFinancing.phase2} />,
      contentComponent: agFinancing.phase2 ? (
        <CheckList
          items={[
            ...(agFinancing.opportunityType !== OpportunityType.BARTER_GUARANTEE
              ? [
                  {
                    text: <ChecklistLink href="#creditAnalysis">{i18next.t('creditAnalysis')}</ChecklistLink>,
                    completed: FinancingProcessUtil.phase2.isPhase2CreditComplete(agFinancing.phase2)
                  }
                ]
              : []),
            ...(canViewDecisionSheet && agFinancing.opportunityType !== OpportunityType.BARTER_GUARANTEE
              ? [
                  {
                    text: <ChecklistLink href="#decisionSheet">{i18next.t('decisionSheet')}</ChecklistLink>,
                    completed: FinancingProcessUtil.phase2.isPhase2CreditApprovalComplete(
                      agFinancing.phase2,
                      creditApprovalDataRound
                    )
                  }
                ]
              : []),
            ...(showGeoreferencing
              ? [
                  {
                    text: <ChecklistLink href="#georeferencing">{i18next.t('georeferencing')}</ChecklistLink>,
                    completed: FinancingProcessUtil.phase2.isPhase2FieldMonitoringComplete(agFinancing.phase2)
                  }
                ]
              : []),
            ...(agFinancing.opportunityType !== OpportunityType.SUPPLIER &&
            agFinancing.opportunityType !== OpportunityType.BARTER_LIMIT
              ? [
                  {
                    text: <ChecklistLink href="#collateralAnalysis">{i18next.t('collateralAnalysis')}</ChecklistLink>,
                    completed: FinancingProcessUtil.phase2.isPhase2CollateralAnalysisComplete(agFinancing.phase2)
                  }
                ]
              : []),
            ...(canViewDecisionSheet && agFinancing.opportunityType === OpportunityType.BARTER_GUARANTEE
              ? [
                  {
                    text: <ChecklistLink href="#decisionSheet">{i18next.t('decisionSheetGuarantee')}</ChecklistLink>,
                    completed: FinancingProcessUtil.phase2.isPhase2CreditApprovalComplete(
                      agFinancing.phase2,
                      creditApprovalDataRound
                    )
                  }
                ]
              : [])
          ]}
        />
      ) : null
    },
    {
      id: '3',
      title: i18next.t('collateralFormalization'),
      path: PhaseRoutes[AgStateName.PHASE_3],
      status: getStatus3(agFinancing.phase3),
      disabled:
        (!agFinancing.currentStates?.includes(AgStateName.PHASE_3) &&
          agFinancing.phase3?.status !== AgFinancingPhase3Status.APPROVED) ||
        isAlternativeFlow,
      component: <Phase3 phaseInfo={agFinancing.phase3} matriculas={agFinancing.phase2?.matriculas} />,
      contentComponent: agFinancing.phase3 ? (
        <CheckList
          items={[
            {
              text: i18next.t('draftCreation'),
              completed: FinancingProcessUtil.phase3.isDraftDone(agFinancing.phase3)
            },
            {
              text: i18next.t('signedDocuments'),
              completed: FinancingProcessUtil.phase3.isSignedDocuments(agFinancing.phase3)
            },
            {
              text: i18next.t('validating'),
              completed: FinancingProcessUtil.phase3.isValidatingDone(agFinancing.phase3, hasConditionals)
            },
            {
              text: i18next.t('receivedPhysicalDocuments'),
              completed: FinancingProcessUtil.phase3.hasReceivedPhysicalDocuments(agFinancing.phase3)
            }
          ]}
        />
      ) : null
    },
    {
      id: '4',
      title: utils.text.toTitleCase(i18next.t('fundAssignment')),
      path: PhaseRoutes[AgStateName.PHASE_4],
      status: getStatus4(agFinancing.phase4),
      disabled: !isPhase3ValidatingDone,
      contentComponent: agFinancing.phase4 ? (
        <CheckList
          items={[
            {
              text: i18next.t('fundAssignment'),
              completed: FinancingProcessUtil.phase4.assignedToFund(agFinancing.phase4)
            },
            {
              text: i18next.t('fundInformationReceive'),
              completed: FinancingProcessUtil.phase4.receivedByFund(agFinancing.phase4)
            },
            {
              text: i18next.t('fundInformationReview'),
              completed: FinancingProcessUtil.phase4.awaitingManagerApproval(agFinancing.phase4)
            },
            {
              text: i18next.t('awaitingApproval'),
              completed: FinancingProcessUtil.phase4.submitted(agFinancing.phase4)
            },
            {
              // Why submitted repeated?, we want to have this final step
              text: i18next.t('submittedMinuta'),
              completed: FinancingProcessUtil.phase4.submitted(agFinancing.phase4)
            }
          ]}
        />
      ) : null,
      component: <Phase4 />
    }
  ];
};

export const Timeline: React.FunctionComponent = () => {
  /**
   * Location
   */
  const location = useLocation();

  /**
   * State
   */
  const [activeStepId, setActiveStepId] = useState<IStep['id']>('1');

  /**
   * Routes Matching
   */
  const { path, url } = useRouteMatch();

  /**
   * History
   */
  const history = useHistory();

  /**
   * Context
   */
  const agFinanceContext = useContext(FinanceProcessContext);
  const { hasPermission } = useContext(UserContext);
  const financingAgFinancing = agFinanceContext.agFinance;
  const { contactRolesBackgroundChecksQuery } = agFinanceContext;
  const { creditApprovalDataQuery } = agFinanceContext;
  const opportunityContactRoles = getUniqueContactRoles(
    contactRolesBackgroundChecksQuery?.data?.opportunityContactRoles ?? []
  );
  const backgroundCheckGroup = contactRolesBackgroundChecksQuery?.data?.backgroundCheckGroup;
  const serasaReport = contactRolesBackgroundChecksQuery?.data?.serasaReport;
  const bacenReport = contactRolesBackgroundChecksQuery?.data?.bacenReport;
  const contactRiskClassification = contactRolesBackgroundChecksQuery?.data?.contactsRiskClassification;
  const creditApprovalDataRound = creditApprovalDataQuery?.data?.creditApprovalData.round;
  const backgroundCheckMap = backgroundCheckGroup?.backgroundChecks
    ? getBackgroundCheckMap(backgroundCheckGroup?.backgroundChecks)
    : undefined;

  const getCurrentPhaseUrl = (): string => {
    if (financingAgFinancing) {
      const cPhase = FinancingProcessUtil.getCurrentPhase(financingAgFinancing);

      return PhaseRoutes[cPhase as PhaseRoutesStageName];
    }

    return PhaseRoutes[AgStateName.PHASE_1]; // defaulting to phase 1 if the context hasnt loaded
  };

  const steps: IStep[] = [];

  // Returns path id that matches with location or first path by default
  const setActiveStepByLocation = () => {
    const phaseOnLocation = location.pathname
      .split('/')
      .slice(-1)
      .pop();

    const asId =
      steps.find(s => {
        if (s.path === phaseOnLocation) {
          return true;
        } else if (s.subSteps) {
          return !!s.subSteps?.find(sub => sub.path && sub.path === phaseOnLocation);
        } else {
          return false;
        }
      })?.id || steps[0].id!;

    setActiveStepId(asId);
  };

  const changeActiveStep = (step: IStep) => {
    setActiveStepId(step.id);

    // Navigate to route if exists
    const selectedStepRoutePath = getStepPath(steps, step);
    if (selectedStepRoutePath) {
      history.push(`${url}/${selectedStepRoutePath}`);
    }
  };

  useEffect(() => {
    if (steps.length) {
      setActiveStepByLocation();
    }
  }, [location, steps]); // eslint-disable-line react-hooks/exhaustive-deps

  const canViewDecisionSheet = hasPermission(Permissions.FA_INTERNAL) || hasPermission(Permissions.FA_ADMIN);
  if (financingAgFinancing) {
    steps.push(
      ...getStepsFromAgFinancing(
        financingAgFinancing,
        opportunityContactRoles,
        backgroundCheckMap,
        serasaReport,
        bacenReport,
        contactRiskClassification,
        canViewDecisionSheet,
        creditApprovalDataRound
      )
    );
  }

  return (
    <div className="flex">
      <Phase1MenuProvider>
        <Stepper steps={steps} activeStepId={activeStepId} onStepClick={changeActiveStep} />
        <div style={{ marginLeft: 50, width: '100%' }}>
          <Switch>
            {steps
              .filter(s => s.path && s.component && !s.disabled)
              .map(stepsWithRoute => (
                <Route key={`route-step-${stepsWithRoute.id}`} path={`${path}/${stepsWithRoute.path}`}>
                  {stepsWithRoute.component}
                </Route>
              ))}
            <Redirect from={path} to={`${path}/${getCurrentPhaseUrl()}`} />
          </Switch>
        </div>
      </Phase1MenuProvider>
    </div>
  );
};
